import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type KeyboardEvent as ReactKeyboardEvent,
  type SVGProps,
} from 'react';
import { createPortal } from 'react-dom';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { cn } from '../cn.js';

export interface Command {
  readonly id: string;
  readonly label: string;
  readonly group: string;
  readonly hint?: string;
  readonly icon?: ComponentType<SVGProps<SVGSVGElement>>;
  readonly keywords?: readonly string[];
  readonly run: () => void;
}

export interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  commands: readonly Command[];
}

/**
 * Ctrl/Cmd-K command palette.
 *
 * Implements the combobox pattern: the input keeps focus and owns the keyboard
 * interaction while `aria-activedescendant` moves the virtual cursor through
 * the listbox. Moving real DOM focus onto the options instead would break
 * type-ahead, which is the whole point of a palette.
 */
export function CommandPalette({ open, onOpenChange, commands }: CommandPaletteProps) {
  const [queryText, setQueryText] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const matches = useMemo(() => rank(commands, queryText), [commands, queryText]);

  // Clamp the cursor when the result set shrinks under it.
  useEffect(() => {
    setActiveIndex((i) => (i >= matches.length ? Math.max(0, matches.length - 1) : i));
  }, [matches.length]);

  useEffect(() => {
    if (!open) return;
    setQueryText('');
    setActiveIndex(0);
    // Focus after paint, or the input is not yet in the document.
    const id = window.requestAnimationFrame(() => inputRef.current?.focus());
    return () => window.cancelAnimationFrame(id);
  }, [open]);

  // Global Ctrl/Cmd-K. Ignored while the user is typing somewhere else so the
  // shortcut cannot swallow a keystroke inside the SQL editor.
  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent): void => {
      if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        onOpenChange(!open);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  const select = useCallback(
    (index: number) => {
      const command = matches[index];
      if (!command) return;
      onOpenChange(false);
      command.run();
    },
    [matches, onOpenChange],
  );

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement>) => {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setActiveIndex((i) => (matches.length === 0 ? 0 : (i + 1) % matches.length));
          break;
        case 'ArrowUp':
          event.preventDefault();
          setActiveIndex((i) =>
            matches.length === 0 ? 0 : (i - 1 + matches.length) % matches.length,
          );
          break;
        case 'Home':
          event.preventDefault();
          setActiveIndex(0);
          break;
        case 'End':
          event.preventDefault();
          setActiveIndex(Math.max(0, matches.length - 1));
          break;
        case 'Enter':
          event.preventDefault();
          select(activeIndex);
          break;
        case 'Escape':
          event.preventDefault();
          onOpenChange(false);
          break;
      }
    },
    [activeIndex, matches.length, onOpenChange, select],
  );

  // Keep the virtual cursor visible when it moves past the viewport edge.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  if (!open) return null;

  let renderedIndex = -1;
  let lastGroup: string | null = null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh] px-4 animate-fade-in">
      <div
        className="absolute inset-0 bg-overlay/70"
        onClick={() => onOpenChange(false)}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-xl bg-surface border border-line rounded-xl shadow-modal overflow-hidden animate-slide-up">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-line">
          <MagnifyingGlassIcon className="w-4 h-4 text-subtle shrink-0" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls="csa-command-list"
            aria-activedescendant={matches.length ? `csa-command-${activeIndex}` : undefined}
            aria-label="Search commands"
            autoComplete="off"
            spellCheck={false}
            placeholder="Search pages and actions\u2026"
            value={queryText}
            onChange={(e) => {
              setQueryText(e.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={onKeyDown}
            className="flex-1 bg-transparent text-sm text-body placeholder:text-subtle focus:outline-none"
          />
          <kbd className="text-[10px] font-mono border border-line rounded px-1 py-0.5 text-subtle">
            Esc
          </kbd>
        </div>

        <ul
          ref={listRef}
          id="csa-command-list"
          role="listbox"
          aria-label="Commands"
          className="max-h-80 overflow-y-auto py-1"
        >
          {matches.length === 0 ? (
            <li className="px-4 py-6 text-sm text-muted text-center">No matching commands</li>
          ) : (
            matches.map((command) => {
              renderedIndex += 1;
              const index = renderedIndex;
              const showGroup = command.group !== lastGroup;
              lastGroup = command.group;
              const Icon = command.icon;

              return (
                <li key={command.id}>
                  {showGroup ? (
                    <p
                      className="px-4 pt-2 pb-1 text-[10px] font-semibold text-subtle uppercase tracking-wider"
                      aria-hidden="true"
                    >
                      {command.group}
                    </p>
                  ) : null}
                  {/*
                    In the combobox pattern the input keeps DOM focus and moves
                    a virtual cursor with aria-activedescendant, so an option
                    must NOT be focusable or type-ahead breaks. Keyboard
                    activation is handled on the input, which is why the two
                    rules below do not apply here.
                  */}
                  {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/interactive-supports-focus */}
                  <div
                    id={`csa-command-${index}`}
                    data-index={index}
                    role="option"
                    aria-selected={index === activeIndex}
                    onMouseMove={() => setActiveIndex(index)}
                    onClick={() => select(index)}
                    className={cn(
                      'mx-1 flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer text-sm',
                      index === activeIndex ? 'bg-brand-subtle text-brand-text' : 'text-body',
                    )}
                  >
                    {Icon ? <Icon className="w-4 h-4 shrink-0" aria-hidden="true" /> : null}
                    <span className="flex-1 truncate">{command.label}</span>
                    {command.hint ? (
                      <span className="text-xs text-subtle shrink-0">{command.hint}</span>
                    ) : null}
                  </div>
                </li>
              );
            })
          )}
        </ul>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Subsequence matching with a small relevance score, which is what makes
 * "aud" find "Audit ledger" and "nq" find "New query". Prefix and word-start
 * matches outrank scattered ones so the obvious answer stays at the top.
 */
function rank(commands: readonly Command[], queryText: string): Command[] {
  const q = queryText.trim().toLowerCase();
  if (!q) return [...commands];

  const scored: { command: Command; score: number }[] = [];

  for (const command of commands) {
    const haystacks = [command.label, command.group, ...(command.keywords ?? [])];
    let best = -1;

    for (const raw of haystacks) {
      const text = raw.toLowerCase();
      if (text.startsWith(q)) {
        best = Math.max(best, 1000);
        continue;
      }
      const wordStart = text.split(/[\s./-]+/).some((w) => w.startsWith(q));
      if (wordStart) {
        best = Math.max(best, 800);
        continue;
      }
      if (text.includes(q)) {
        best = Math.max(best, 600);
        continue;
      }
      const sub = subsequenceScore(text, q);
      if (sub >= 0) best = Math.max(best, sub);
    }

    if (best >= 0) scored.push({ command, score: best });
  }

  return scored
    .sort((a, b) => b.score - a.score || a.command.label.localeCompare(b.command.label))
    .map((s) => s.command);
}

/** Returns a score, or -1 when `needle` is not a subsequence of `haystack`. */
function subsequenceScore(haystack: string, needle: string): number {
  let hi = 0;
  let score = 0;
  let lastMatch = -1;

  for (const ch of needle) {
    const found = haystack.indexOf(ch, hi);
    if (found === -1) return -1;
    // Adjacent matches are worth more than scattered ones.
    score += lastMatch === found - 1 ? 10 : 1;
    lastMatch = found;
    hi = found + 1;
  }
  return score;
}
